import React from 'react';
import { Link } from 'react-router-dom';
import { Phone, Mail, MapPin, ShieldCheck, ExternalLink, Lock } from 'lucide-react';

const Footer = ({ language }) => { 

  const t = {
    es: {
      tagline: "El Departamento de RRHH y SST Externo de las PyMEs en Colombia.",
      description: "Protegemos tu empresa contra multas del Ministerio, demandas laborales y errores en nómina. Operas tranquilo mientras tú te concentras en vender y crecer.",
      contactTitle: "Atención Inmediata",
      citiesTitle: "Cobertura Nacional",
      coverage: "Todo el Valle de Aburrá",
      backingTitle: "Respaldo e Integridad",
      fenalcoText: "Empresa orgullosamente afiliada a FENALCO Antioquia. Compromiso ético y empresarial respaldado.",
      privacyPolicy: "Política de Tratamiento de Datos",
      rights: "Todos los derechos reservados. Blindando empresas en Colombia."
    },
    en: {
      tagline: "The External HR & Safety Department for Colombian SMEs.",
      description: "We protect your company against Ministry fines, labor lawsuits, and payroll errors. Operate with peace of mind while you focus on growth.",
      contactTitle: "Direct Contact",
      citiesTitle: "National Coverage",
      coverage: "Entire Aburrá Valley",
      backingTitle: "Trust & Backing",
      fenalcoText: "Proudly affiliated with FENALCO. Backed by ethical and corporate standards.",
      privacyPolicy: "Data Privacy Policy",
      rights: "All rights reserved. Protecting businesses in Colombia."
    }
  };

  const text = t[language] || t.es;

  // URL OFICIAL DEL LOGO AZUL ZAPHY Y FENALCO
  const zaphyBlueLogoUrl = "https://zaphy.com.co/wp-content/uploads/2025/05/Logo-Zaphy-TX-Blanco-logo-azul.png";
  const fenalcoLogoUrl = "https://zaphy.com.co/wp-content/uploads/2026/07/portalResource.png";

  return (
    <footer className="bg-slate-950 text-white pt-20 pb-10 relative overflow-hidden border-t border-purple-900/40 font-[Poppins]">
      
      {/* Decoración de fondo con brillo morado y cian sutil */}
      <div className="absolute top-0 left-1/4 w-96 h-96 bg-purple-900/10 rounded-full blur-3xl pointer-events-none"></div>
      <div className="absolute bottom-0 right-10 w-80 h-80 bg-cyan-900/10 rounded-full blur-3xl pointer-events-none"></div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        
        {/* GRID DE 4 COLUMNAS DE ALTO IMPACTO */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-12 gap-10 lg:gap-8 mb-16">
          
          {/* COLUMNA 1: LOGO AZUL ZAPHY Y PROPUESTA DE VALOR HORMOZI (4 cols) */}
          <div className="lg:col-span-4 space-y-5">
            <Link to="/" className="inline-block group">
              <img 
                src={zaphyBlueLogoUrl} 
                className="h-16 w-auto object-contain transition-transform duration-300 group-hover:scale-105" 
                alt="Zaphy Gestión Integral - Logo Oficial"
              />
            </Link>

            <p className="text-cyan-400 font-bold text-xs uppercase tracking-wider">
              {text.tagline}
            </p>

            <p className="text-slate-400 text-sm leading-relaxed text-justify">
              {text.description}
            </p>
          </div>

          {/* COLUMNA 2: CONTACTO DIRECTO (3 cols) */}
          <div className="lg:col-span-3 space-y-5">
            <h4 className="font-bold text-lg text-white border-b-2 border-purple-600 pb-2 inline-block">
              {text.contactTitle}
            </h4>
            <div className="space-y-3.5 text-sm">
              <a 
                href="https://wa.me/573114044203" 
                target="_blank" 
                rel="noopener noreferrer"
                className="flex items-center gap-3 text-slate-300 hover:text-cyan-400 transition-colors group"
              >
                <div className="w-9 h-9 rounded-xl bg-slate-900 border border-slate-800 flex items-center justify-center text-cyan-400 group-hover:border-cyan-500/50 shadow-inner">
                  <Phone size={17}/> 
                </div>
                <span className="font-semibold">311 404 4203</span>
              </a>

              <a 
                href="https://wa.me/573007503796" 
                target="_blank" 
                rel="noopener noreferrer"
                className="flex items-center gap-3 text-slate-300 hover:text-cyan-400 transition-colors group"
              >
                <div className="w-9 h-9 rounded-xl bg-slate-900 border border-slate-800 flex items-center justify-center text-cyan-400 group-hover:border-cyan-500/50 shadow-inner">
                  <Phone size={17}/> 
                </div>
                <span className="font-semibold">300 750 3796</span>
              </a>

              <a 
                href="mailto:comercial@zaphy.com.co" 
                className="flex items-center gap-3 text-slate-300 hover:text-cyan-400 transition-colors group"
              >
                <div className="w-9 h-9 rounded-xl bg-slate-900 border border-slate-800 flex items-center justify-center text-cyan-400 group-hover:border-cyan-500/50 shadow-inner">
                  <Mail size={17}/> 
                </div>
                <span className="font-medium text-xs">comercial@zaphy.com.co</span>
              </a>
            </div>
          </div>

          {/* COLUMNA 3: COBERTURA Y CIUDADES (2 cols) */}
          <div className="lg:col-span-2 space-y-5">
            <h4 className="font-bold text-lg text-white border-b-2 border-purple-600 pb-2 inline-block">
              {text.citiesTitle}
            </h4>
            <ul className="space-y-2.5 text-slate-300 text-sm">
              <li className="flex items-center gap-2 font-medium">
                <MapPin size={15} className="text-purple-400"/> Bogotá D.C.
              </li>
              <li className="flex items-center gap-2 font-medium">
                <MapPin size={15} className="text-purple-400"/> Medellín
              </li>
              <li className="flex items-center gap-2 font-medium">
                <MapPin size={15} className="text-purple-400"/> Cali
              </li>
              <li className="flex items-center gap-2 text-xs text-slate-400 pt-2 border-t border-slate-900">
                <ShieldCheck size={14} className="text-cyan-400"/> {text.coverage}
              </li>
            </ul>
          </div>

          {/* COLUMNA 4: RESPALDO OFICIAL FENALCO (3 cols) */}
          <div className="lg:col-span-3 space-y-5">
            <h4 className="font-bold text-lg text-white border-b-2 border-purple-600 pb-2 inline-block">
              {text.backingTitle}
            </h4>
            
            {/* TARJETA / INSIGNIA FENALCO CON CÍRCULO Y EFECTO GLASS */}
            <div className="bg-slate-900/90 border border-slate-800 p-4 rounded-2xl shadow-lg relative group hover:border-purple-500/50 transition-all">
              <div className="flex items-center gap-3.5 mb-2.5">
                {/* Circulo Blanco de Fenalco */}
                <div className="w-14 h-14 rounded-full bg-white p-2 flex items-center justify-center shadow-md shrink-0 border border-slate-200">
                  <img 
                    src={fenalcoLogoUrl} 
                    alt="Afiliados a Fenalco Antioquia" 
                    className="max-h-full max-w-full object-contain"
                  />
                </div>
                <div>
                  <span className="text-[10px] font-black uppercase text-cyan-400 tracking-wider block">
                    Afiliado Oficial
                  </span>
                  <h5 className="text-sm font-bold text-white leading-tight">
                    FENALCO Antioquia
                  </h5>
                </div>
              </div>
              
              <p className="text-slate-400 text-xs leading-relaxed">
                {text.fenalcoText}
              </p>
            </div>
          </div>

        </div>

        {/* BARRA INFERIOR DE DERECHOS, POLÍTICAS Y CRÉDITOS */}
        <div className="border-t border-slate-900 pt-8 flex flex-col md:flex-row justify-between items-center text-xs text-slate-500 gap-4">
          <p className="text-center md:text-left">
            &copy; {new Date().getFullYear()} <strong className="text-slate-300">Zaphy Gestión Integral S.A.S.</strong> {text.rights}
          </p>
          
          <div className="flex flex-wrap items-center justify-center md:justify-end gap-4 md:gap-6 text-slate-400">
            <Link to="/blog" className="hover:text-cyan-400 transition-colors">
              Blog Corporativo
            </Link>

            <span>•</span>

            {/* ENLACE A POLÍTICA DE TRATAMIENTO DE DATOS (HABEAS DATA) */}
            <Link to="/politica" className="hover:text-cyan-400 transition-colors flex items-center gap-1.5 text-slate-300 font-medium">
              <Lock size={12} className="text-cyan-400" />
              {text.privacyPolicy}
            </Link>

            <span>•</span>

            <a href="https://wa.me/573114044203" target="_blank" rel="noopener noreferrer" className="hover:text-cyan-400 transition-colors flex items-center gap-1">
              Soporte Empresarial <ExternalLink size={12}/>
            </a>
          </div>
        </div>

      </div>
    </footer>
  );
};

export default Footer;